<script setup lang="ts">
import { useTenantStore } from '@/stores/tenantStore';
import { useAuthStore } from '@/stores/authStore';
import { useCustomizerStore } from '@/stores/customizer';
import { getImageUrl } from '@/utils/imageUrl';
import { Icon } from '@iconify/vue';
import { computed, ref, watch } from 'vue';


const tenantStore = useTenantStore();
const authStore = useAuthStore();
const customizer = useCustomizerStore();

const user = computed(() => authStore.user);

// Nome completo do usuário (prioridade: display_name > given_name + surname > username)
const userName = computed(() => {
    if (user.value?.display_name) {
        return user.value.display_name; // "Rafael Lobo" - do banco
    }
    if (user.value?.given_name && user.value?.surname) {
        return `${user.value.given_name} ${user.value.surname}`;
    }
    if (user.value?.username) {
        return user.value.username;
    }
    return 'Colaborador';
});

// Email do usuário
const userEmail = computed(() => user.value?.email || 'email@exemplo.com');

// Cargo/Função do usuário
const userTitle = computed(() => user.value?.title || 'Não informado');

// Departamento do usuário
const userDepartment = computed(() => user.value?.department || 'Não informado');

// Empresa do usuário
const userCompany = computed(() => user.value?.company || tenantStore.nome);

// Role baseado em grupos
const userRole = computed(() => (user.value?.groups?.includes('admin') ? 'Administrador' : 'Colaborador'));

// Iniciais do usuário (prioridade: given_name + surname > display_name > username)
const userInitials = computed(() => {
    if (user.value?.given_name && user.value?.surname) {
        return (user.value.given_name[0] + user.value.surname[0]).toUpperCase(); // "RL"
    }
    if (user.value?.display_name) {
        return user.value.display_name
            .split(' ')
            .map((name) => name[0])
            .join('')
            .toUpperCase();
    }
    if (user.value?.username) {
        return user.value.username
            .split(' ')
            .map((name) => name[0])
            .join('')
            .toUpperCase();
    }
    return 'U';
});

// Cor do avatar baseada em grupos
const avatarColor = computed(() => (user.value?.groups?.includes('admin') ? 'primary' : 'secondary'));

// Computed para verificar se é adminMaster
const isAdminMaster = computed(() => authStore.isAdminMaster);

// Avatar do usuário (foto ou iniciais)
const userAvatar = computed(() => {
    if (user.value?.photo_url) return getImageUrl(user.value.photo_url);
    if (user.value?.photo) return getImageUrl(user.value.photo);
    return null; // Retorna null para usar iniciais
});

// Estado para controlar se a foto falhou ao carregar
const photoError = ref(false);

// Handler para quando a foto falhar ao carregar
const handlePhotoError = () => {
    photoError.value = true;
};

// Resetar erro quando a foto mudar
watch(userAvatar, () => {
    photoError.value = false;
});

const profileDD = computed(() => {
    const items = [
        {
            title: 'Meu Perfil',
            subtitle: 'Acesse suas informações pessoais',
            avatar: 'user-line-duotone',
            bgcolor: 'secondary',
            href: '/profile'
        },
        {
            title: 'Custumização',
            subtitle: 'Customize a aparencia do seu sistema',
            avatar: 'settings-line-duotone',
            bgcolor: 'secondary',
            href: '#'
        }
    ];
    return items;
});
</script>

<template>
    <!-- Profile Dropdown -->
    <v-menu :close-on-content-click="true" class="profile_popup">
        <template v-slot:activator="{ props }">
            <div class="text-left px-0 cursor-pointer" variant="text" v-bind="props">
                <div class="d-flex align-center">
                    <!-- ✅ Avatar com foto ou iniciais -->
                    <v-avatar size="50" :color="userAvatar && !photoError ? undefined : avatarColor">
                        <v-img v-if="userAvatar && !photoError" :src="userAvatar" :alt="userName" cover @error="handlePhotoError" />
                        <span v-else class="text-h6 font-weight-bold text-white">{{ userInitials }}</span>
                    </v-avatar>

                    <div class="ml-md-4 d-md-block d-none profile-header-info">
                        <h6 class="text-h6 d-flex align-center text-black font-weight-semibold profile-header-name">
                            {{ userName }}
                        </h6>
                        <span class="text-subtitle-2 font-weight-medium text-grey100 profile-header-title">
                            {{ userTitle }}
                        </span>
                    </div>
                </div>
            </div>
        </template>

        <v-sheet rounded="lg" width="385" elevation="10" class="mt-5 profile-sheet">
            <div class="px-8 pt-6">
                <div class="d-flex align-center py-3">
                    <div class="profile-info">
                        <h6 class="text-h5 mb-1 profile-name">{{ userName }}</h6>
                        <span class="text-subtitle-1 font-weight-regular text-grey100 font-weight-medium profile-title">
                            {{ userTitle }}
                        </span>
                        <div class="d-flex align-center mt-1">
                            <span class="text-subtitle-1 text-grey100 font-weight-medium profile-email">
                                {{ userEmail }}
                            </span>
                        </div>
                        <div class="text-caption text-grey100 mt-1 profile-department">{{ userDepartment }} • {{ userCompany }}</div>
                    </div>
                </div>
                <v-divider></v-divider>
            </div>
            <!-- Menu de opções -->
            <v-list class="pb-5 theme-list" lines="two">
                <v-list-item
                    v-for="item in profileDD"
                    :key="item.title"
                    class="py-4 px-8 custom-text-primary"
                    :to="item.href === '#' ? undefined : item.href"
                    @click="item.href === '#' ? customizer.SET_CUSTOMIZER_DRAWER(!customizer.Customizer_drawer) : null"
                    :class="item.bgcolor === 'error' ? 'red-50' : ''"
                >
                    <template v-slot:prepend>
                        <v-avatar size="40" class="rounded-lg" :class="'bg-light' + item.bgcolor">
                            <Icon :icon="'solar:' + item.avatar" width="25" height="25" :class="'text-' + item.bgcolor" />
                        </v-avatar>
                    </template>
                    <div class="profile-menu-item">
                        <h6 class="text-h6 font-weight-medium mb-1 custom-title profile-menu-title">{{ item.title }}</h6>
                        <p class="text-subtitle-1 font-weight-regular text-grey100 profile-menu-subtitle">{{ item.subtitle }}</p>
                    </div>
                </v-list-item>
            </v-list>
            <div class="pb-6 px-8 text-center">
                <v-btn color="primary" size="large" rounded="pill" block @click="authStore.logout()">
                    Logout <Icon icon="mdi:logout" class="ml-2" width="20" height="20" />
                </v-btn>
            </div>
        </v-sheet>
    </v-menu>
</template>

<style scoped lang="scss">
.profile-sheet {
    overflow-x: hidden !important;
    max-width: 385px;
}

.profile-header-info {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    max-width: 200px;
}

.profile-header-name,
.profile-header-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
    max-width: 100%;
}

.profile-info {
    min-width: 0;
    flex: 1;
    overflow: hidden;
}

.profile-name,
.profile-title,
.profile-email,
.profile-department {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
    max-width: 100%;
}

.profile-menu-item {
    min-width: 0;
    flex: 1;
    overflow: hidden;
}

.profile-menu-title,
.profile-menu-subtitle {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
    max-width: 100%;
}

.profile-menu-subtitle {
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
